import React, { useEffect } from 'react';
import { Button, Card, DatePicker, Form, Input, InputNumber, Select, Space } from 'antd';
import dayjs from 'dayjs';
import { useCreateCampaign } from '../hooks/useCreateCampaign';
import { useUpdateCampaign } from '../hooks/useUpdateCampaign';
import type { CampaignFormValues, CampaignStatus } from '../types';
import { CAMPAIGN_STATUS_OPTIONS } from '../constants';
import { useLeadSources } from '../../leads/hooks/useLeadSources';

export interface CampaignFormProps {
  mode: 'add' | 'edit';
  editCampaignId?: string | null;
  initialValues?: Partial<CampaignFormValues> | null;
  onSuccess?: () => void;
  onCancel?: () => void;
}

const CampaignForm: React.FC<CampaignFormProps> = ({
  mode,
  editCampaignId,
  initialValues,
  onSuccess,
  onCancel,
}) => {
  const [form] = Form.useForm();
  const createMutation = useCreateCampaign(onSuccess);
  const updateMutation = useUpdateCampaign(editCampaignId ?? null, onSuccess);
  const { data: sources, isLoading: sourcesLoading, isError: sourcesError } = useLeadSources();
  const channelOptions =
    sources?.filter((s) => s.isActive).map((s) => ({ value: s.id, label: s.label })) ?? [];

  useEffect(() => {
    if (mode === 'edit' && initialValues) {
      const tagsVal = initialValues.tags;
      const tagsDisplay = Array.isArray(tagsVal) && tagsVal.length > 0 ? tagsVal.join(', ') : undefined;
      form.setFieldsValue({
        name: initialValues.name,
        description: initialValues.description ?? undefined,
        channelId: initialValues.channelId,
        status: initialValues.status ?? 0,
        startDateUtc: initialValues.startDateUtc ? dayjs(initialValues.startDateUtc) : undefined,
        endDateUtc: initialValues.endDateUtc ? dayjs(initialValues.endDateUtc) : undefined,
        budget: initialValues.budget ?? undefined,
        targetedAmountGoal: initialValues.targetedAmountGoal ?? undefined,
        tags: tagsDisplay,
      });
    }
  }, [mode, initialValues, form]);

  const handleFinish = (values: Record<string, unknown>) => {
    const startDate = values.startDateUtc as dayjs.Dayjs | undefined;
    const endDate = values.endDateUtc as dayjs.Dayjs | undefined;
    const tagsRaw = values.tags;
    const tagsArray: string[] = Array.isArray(tagsRaw)
      ? (tagsRaw as string[])
      : typeof tagsRaw === 'string' && tagsRaw.trim()
        ? tagsRaw.split(',').map((s) => s.trim()).filter(Boolean)
        : [];
    const payload: CampaignFormValues = {
      name: values.name as string,
      description: (values.description as string) ?? undefined,
      channelId: values.channelId as number,
      status: (values.status ?? 0) as CampaignStatus,
      startDateUtc: startDate?.toISOString(),
      endDateUtc: endDate?.toISOString(),
      budget: values.budget as number | undefined,
      targetedAmountGoal: values.targetedAmountGoal as number | undefined,
      tags: tagsArray,
    };
    if (mode === 'edit' && editCampaignId) {
      updateMutation.mutate(payload);
    } else {
      createMutation.mutate(payload);
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  return (
    <Form
      form={form}
      layout="vertical"
      onFinish={handleFinish}
      initialValues={{
        status: 0,
      }}
    >
      <Card title="Details" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Form.Item
          name="name"
          label="Name"
          rules={[{ required: true, message: 'Please enter a campaign name' }]}
        >
          <Input placeholder="E.g. Q1 Website Promo" />
        </Form.Item>
        <Form.Item name="description" label="Description">
          <Input.TextArea rows={2} placeholder="Optional description" />
        </Form.Item>
      </Card>

      <Card title="Channel & status" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Form.Item
          name="channelId"
          label="Channel"
          rules={[{ required: true, message: 'Select a channel' }]}
          extra={
            sourcesError
              ? 'Could not load channels. Refresh and try again.'
              : !sourcesLoading && channelOptions.length === 0
                ? 'No channels available for this workspace. They are created when your organization is provisioned (e.g. Website, Referral, Campaign).'
                : undefined
          }
        >
          <Select
            options={channelOptions}
            placeholder="Select channel"
            loading={sourcesLoading}
            disabled={sourcesLoading || channelOptions.length === 0}
            notFoundContent={sourcesLoading ? 'Loading…' : 'No channels found'}
          />
        </Form.Item>
        <Form.Item name="status" label="Status" rules={[{ required: true }]}>
          <Select options={CAMPAIGN_STATUS_OPTIONS} placeholder="Select status" />
        </Form.Item>
      </Card>

      <Card title="Dates" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Form.Item name="startDateUtc" label="Start Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
        <Form.Item name="endDateUtc" label="End Date">
          <DatePicker style={{ width: '100%' }} />
        </Form.Item>
      </Card>

      <Card title="Budget & goal" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Form.Item name="budget" label="Budget">
          <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="0.00" />
        </Form.Item>
        <Form.Item name="targetedAmountGoal" label="Targeted Amount Goal">
          <InputNumber style={{ width: '100%' }} min={0} step={0.01} placeholder="0.00" />
        </Form.Item>
      </Card>

      <Card title="Tags" size="small" className="cp-form-section" style={{ marginBottom: 16 }}>
        <Form.Item name="tags" label="Tags (comma-separated)">
          <Input placeholder="e.g. q1, website, promo" />
        </Form.Item>
      </Card>

      <Form.Item style={{ marginBottom: 0 }}>
        <Space>
          <Button type="primary" htmlType="submit" loading={saving}>
            Save
          </Button>
          <Button onClick={onCancel}>Cancel</Button>
        </Space>
      </Form.Item>
    </Form>
  );
};

export default CampaignForm;
